import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR, fmtDate } from "../lib/format";
import { toast } from "sonner";
import { PackagePlus, ShoppingCart, PackageCheck, PlusCircle, Trash2, ArrowRight, ClipboardList, Truck, Receipt } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

const STAGES = [
    { key: "req", label: "1 · Requisitions", icon: ClipboardList, hint: "Request material" },
    { key: "po", label: "2 · Purchase Orders", icon: ShoppingCart, hint: "Order from vendor" },
    { key: "grn", label: "3 · Goods Receipts", icon: PackageCheck, hint: "Receive & inventory-in" },
    { key: "bill", label: "4 · Bill & Pay", icon: Receipt, hint: "Post bill + settle" },
];

export default function Procurement() {
    const [tab, setTab] = useState("req");
    const [reqs, setReqs] = useState([]);
    const [pos, setPos] = useState([]);
    const [grns, setGrns] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [projects, setProjects] = useState([]);
    const [showForm, setShowForm] = useState(null); // "req" | "po" | "grn" | null

    const refresh = async () => {
        const [r, p, g, v, prj] = await Promise.all([
            api.get("/procurement/requisitions"),
            api.get("/procurement/pos"),
            api.get("/procurement/grns"),
            api.get("/entry/vendors"),
            api.get("/projects"),
        ]);
        setReqs(r.data.requisitions);
        setPos(p.data.pos);
        setGrns(g.data.grns);
        setVendors(v.data.vendors);
        setProjects(prj.data.projects);
    };

    useEffect(() => { refresh(); }, []);

    const summary = {
        req_open: reqs.filter((r) => r.status === "Open").length,
        req_converted: reqs.filter((r) => r.status === "Converted").length,
        po_open_value: pos.filter((p) => p.status !== "Closed").reduce((s, p) => s + (p.total_value || 0), 0),
        grn_value: grns.reduce((s, g) => s + (g.total_value || 0), 0),
    };

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// PROCUREMENT · PROCURE-TO-PAY</div>
                    <h1 className="font-display font-black text-3xl flex items-center gap-3"><ShoppingCart className="text-[var(--brand-amber)]" size={26} /> Requisition → PO → GRN → Bill → Payment</h1>
                    <div className="font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider mt-1">
                        Every step persists to database · Bill posting via <Link to="/entry" className="text-[var(--brand-amber)] hover:underline">Quick Entry / PURCHASE</Link> · Payment via <Link to="/entry" className="text-[var(--brand-amber)] hover:underline">Quick Entry / PAYMENT</Link>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard testId="pc-req-open" label="Open Requisitions" value={summary.req_open} format="custom" accent />
                <KpiCard testId="pc-req-converted" label="Converted to PO" value={summary.req_converted} format="custom" />
                <KpiCard testId="pc-po-open" label="Open PO Commitment" value={summary.po_open_value} sub={`${pos.length} POs`} />
                <KpiCard testId="pc-grn-value" label="Total GRN Value" value={summary.grn_value} sub={`${grns.length} received`} />
            </div>

            {/* Workflow stages */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {STAGES.map((s, idx) => {
                    const Icon = s.icon;
                    return (
                        <button
                            key={s.key}
                            data-testid={`pc-tab-${s.key}`}
                            onClick={() => setTab(s.key)}
                            className={`border p-3 text-left transition-colors relative ${tab === s.key ? "border-[var(--brand-amber)] bg-[var(--brand-amber-dim)]" : "border-panel hover:border-[var(--brand-amber)]"}`}
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <Icon size={14} className={tab === s.key ? "text-[var(--brand-amber)]" : "text-[var(--text-secondary)]"} />
                                <div className={`text-xs font-mono-fin uppercase tracking-[0.15em] ${tab === s.key ? "text-[var(--brand-amber)]" : "text-[var(--text-primary)]"}`}>{s.label}</div>
                            </div>
                            <div className="text-[10px] font-mono-fin text-[var(--text-secondary)] uppercase tracking-wider">{s.hint}</div>
                            {idx < STAGES.length - 1 && (
                                <ArrowRight size={12} className="absolute -right-3 top-1/2 -translate-y-1/2 text-[var(--brand-amber)] hidden md:block" />
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Content per tab */}
            {tab === "req" && (
                <>
                    <div className="flex justify-end">
                        <button data-testid="pc-new-req" onClick={() => setShowForm("req")} className="px-3 py-1.5 bg-[var(--brand-amber)] text-black font-mono-fin uppercase text-[10px] tracking-[0.15em] font-semibold flex items-center gap-1 hover:bg-[var(--bg-panel-hover)]">
                            <PlusCircle size={11} /> New Requisition
                        </button>
                    </div>
                    <ListTable
                        testId="pc-req-list"
                        title="Purchase Requisitions"
                        subtitle={`${reqs.length} total · Persisted to database`}
                        items={reqs}
                        columns={[
                            { key: "req_no", label: "Req No", cls: "text-[var(--brand-amber)]" },
                            { key: "date", label: "Date", fmt: fmtDate, cls: "text-[var(--text-secondary)]" },
                            { key: "department", label: "Dept" },
                            { key: "requested_by", label: "Requested By" },
                            { key: "priority", label: "Priority" },
                            { key: "lines", label: "Items", fmt: (l) => l.length + " lines" },
                            { key: "total_est_value", label: "Est Value (₹)", fmt: fmtINR, cls: "text-right text-amber" },
                            { key: "status", label: "Status" },
                        ]}
                        onDelete={async (id) => { await api.delete(`/procurement/requisitions/${id}`); toast.success("Deleted"); refresh(); }}
                        emptyText="No requisitions yet. Click 'New Requisition' to raise one."
                    />
                </>
            )}

            {tab === "po" && (
                <>
                    <div className="flex justify-end">
                        <button data-testid="pc-new-po" onClick={() => setShowForm("po")} className="px-3 py-1.5 bg-[var(--brand-amber)] text-black font-mono-fin uppercase text-[10px] tracking-[0.15em] font-semibold flex items-center gap-1 hover:bg-[var(--bg-panel-hover)]">
                            <PlusCircle size={11} /> New Purchase Order
                        </button>
                    </div>
                    <ListTable
                        testId="pc-po-list"
                        title="Purchase Orders"
                        subtitle={`${pos.length} total · GST included in Total Value`}
                        items={pos}
                        columns={[
                            { key: "po_no", label: "PO No", cls: "text-[var(--brand-amber)]" },
                            { key: "date", label: "Date", fmt: fmtDate, cls: "text-[var(--text-secondary)]" },
                            { key: "vendor_name", label: "Vendor" },
                            { key: "project_code", label: "Project" },
                            { key: "lines", label: "Items", fmt: (l) => l.length + " lines" },
                            { key: "subtotal", label: "Subtotal", fmt: fmtINR, cls: "text-right" },
                            { key: "total_value", label: "Total (₹)", fmt: fmtINR, cls: "text-right text-amber" },
                            { key: "status", label: "Status" },
                        ]}
                        onDelete={async (id) => { await api.delete(`/procurement/pos/${id}`); toast.success("Deleted"); refresh(); }}
                        emptyText="No POs yet. Click 'New Purchase Order' to raise one."
                        extraActions={(row) => (
                            <>
                                <a
                                    href={`${process.env.REACT_APP_BACKEND_URL}/api/procurement/pos/${row.id}/pdf`}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`po-pdf-${row.id}`}
                                    title="Download PO PDF"
                                    className="text-[var(--text-muted)] hover:text-[var(--brand-amber)] px-1"
                                >
                                    📄
                                </a>
                                <button
                                    data-testid={`po-email-${row.id}`}
                                    onClick={async () => {
                                        try {
                                            const r = await api.post(`/procurement/pos/${row.id}/email`, {});
                                            if (r.data.sent) {
                                                toast.success(`📧 Emailed PO to ${r.data.recipient}`);
                                            } else {
                                                toast.warning(`Not sent: ${r.data.reason || "unknown"}`);
                                            }
                                        } catch (e) {
                                            toast.error(e.response?.data?.detail || e.message);
                                        }
                                    }}
                                    title="Email PO to vendor"
                                    className="text-[var(--text-muted)] hover:text-[var(--brand-amber)] px-1"
                                >
                                    ✉
                                </button>
                            </>
                        )}
                    />
                </>
            )}

            {tab === "grn" && (
                <>
                    <div className="flex justify-end">
                        <button data-testid="pc-new-grn" onClick={() => setShowForm("grn")} className="px-3 py-1.5 bg-[var(--brand-amber)] text-black font-mono-fin uppercase text-[10px] tracking-[0.15em] font-semibold flex items-center gap-1 hover:bg-[var(--bg-panel-hover)]">
                            <PlusCircle size={11} /> New GRN
                        </button>
                    </div>
                    <ListTable
                        testId="pc-grn-list"
                        title="Goods Receipt Notes (GRN)"
                        subtitle={`${grns.length} total · Each GRN auto-posts Dr Inventory / Cr GRN Clearing`}
                        items={grns}
                        columns={[
                            { key: "grn_no", label: "GRN No", cls: "text-[var(--brand-amber)]" },
                            { key: "date", label: "Date", fmt: fmtDate },
                            { key: "vendor_name", label: "Vendor" },
                            { key: "po_no", label: "PO Ref" },
                            { key: "project_code", label: "Project" },
                            { key: "lines", label: "Items", fmt: (l) => l.length + " lines" },
                            { key: "total_value", label: "Value (₹)", fmt: fmtINR, cls: "text-right text-amber" },
                            { key: "status", label: "Status" },
                        ]}
                        onDelete={async (id) => { await api.delete(`/procurement/grns/${id}`); toast.success("Deleted"); refresh(); }}
                        emptyText="No GRNs yet. Click 'New GRN' when material is received against a PO."
                    />
                </>
            )}

            {tab === "bill" && (
                <Panel testId="pc-bill-info" title="Bill & Pay Step" subtitle="Handled via Quick Entry so accounting stays in one engine">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Link to="/entry" data-testid="pc-goto-bill" className="border border-[var(--brand-amber)] p-4 hover:bg-[var(--brand-amber-dim)]">
                            <div className="flex items-center gap-2"><Receipt size={16} className="text-[var(--brand-amber)]" /><div className="text-sm font-body text-[var(--text-primary)]">Post Purchase Bill</div></div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mt-2">→ Quick Entry / PURCHASE tab · Dr Expense/Inventory + Dr GST Input / Cr AP + Cr TDS Payable</div>
                        </Link>
                        <Link to="/entry" data-testid="pc-goto-pay" className="border border-[var(--brand-amber)] p-4 hover:bg-[var(--brand-amber-dim)]">
                            <div className="flex items-center gap-2"><Truck size={16} className="text-[var(--brand-amber)]" /><div className="text-sm font-body text-[var(--text-primary)]">Make Vendor Payment</div></div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mt-2">→ Quick Entry / PAYMENT tab · Dr AP / Cr Bank · Settle against a specific bill</div>
                        </Link>
                    </div>
                    <div className="mt-4 text-[10px] font-mono-fin text-[var(--text-muted)] uppercase tracking-wider italic">
                        Note: The purchase bill can reference this PO/GRN via the invoice_no field. Payment can settle any open bill from Payables.
                    </div>
                </Panel>
            )}

            {/* Modals */}
            {showForm === "req" && <RequisitionForm projects={projects} onClose={() => setShowForm(null)} onSaved={() => { setShowForm(null); refresh(); }} />}
            {showForm === "po" && <POForm vendors={vendors} projects={projects} requisitions={reqs.filter((r) => r.status === "Open")} onClose={() => setShowForm(null)} onSaved={() => { setShowForm(null); refresh(); }} />}
            {showForm === "grn" && <GRNForm pos={pos.filter((p) => p.status !== "Closed")} onClose={() => setShowForm(null)} onSaved={() => { setShowForm(null); refresh(); }} />}
        </div>
    );
}


function ListTable({ testId, title, subtitle, items, columns, onDelete, emptyText, extraActions }) {
    return (
        <Panel testId={testId} title={title} subtitle={subtitle}>
            {items.length === 0 ? (
                <div className="text-center py-8 text-[var(--text-secondary)] font-mono-fin text-xs uppercase tracking-wider">{emptyText}</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full font-mono-fin text-xs">
                        <thead className="border-b border-panel">
                            <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                {columns.map((c) => <th key={c.key} className={`py-2 ${c.cls?.includes("right") ? "text-right" : "text-left"}`}>{c.label}</th>)}
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it) => (
                                <tr key={it.id} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)]">
                                    {columns.map((c) => (
                                        <td key={c.key} className={`py-2 ${c.cls || "text-[var(--text-primary)]"}`}>
                                            {c.fmt ? c.fmt(it[c.key]) : (it[c.key] ?? "—")}
                                        </td>
                                    ))}
                                    <td className="text-right whitespace-nowrap">
                                        {extraActions && extraActions(it)}
                                        <button onClick={() => onDelete(it.id)} data-testid={`pc-del-${it.id}`} className="text-[var(--text-muted)] hover:text-neg px-1" title="Delete"><Trash2 size={12} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Panel>
    );
}


function Modal({ title, children, onClose }) {
    return (
        <div className="fixed inset-0 z-40 bg-[var(--bg-base)]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-panel border border-[var(--brand-amber)]" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-panel border-b border-panel px-4 py-3 flex items-center justify-between">
                    <div className="text-xs font-mono-fin uppercase tracking-[0.15em] text-[var(--brand-amber)]">{title}</div>
                    <button onClick={onClose} data-testid="pc-modal-close" className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]">✕</button>
                </div>
                <div className="p-4">{children}</div>
            </div>
        </div>
    );
}

const inputCls = "w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin text-[var(--text-primary)] focus:border-[var(--brand-amber)] outline-none";


function RequisitionForm({ projects, onClose, onSaved }) {
    const [form, setForm] = useState({ date: today(), department: "Projects", requested_by: "", priority: "Normal", project_id: "", remarks: "", lines: [{ item: "", unit: "Bags", qty: 1, est_rate: 0 }] });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const submit = async () => {
        try {
            const project = projects.find((p) => p.id === form.project_id);
            const res = await api.post("/procurement/requisitions", {
                ...form,
                project_code: project?.code || "",
                lines: form.lines.map((l) => ({ ...l, qty: Number(l.qty), est_rate: Number(l.est_rate) })),
            });
            toast.success(`${res.data.req_no} saved`);
            onSaved();
        } catch (e) {
            toast.error("Failed: " + (e.response?.data?.detail || e.message));
        }
    };
    return (
        <Modal title="New Purchase Requisition" onClose={onClose}>
            <div className="grid grid-cols-2 gap-3">
                <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} data-testid="pc-req-date" />
                <input placeholder="Requested by" value={form.requested_by} onChange={(e) => set("requested_by", e.target.value)} className={inputCls} data-testid="pc-req-by" />
                <select value={form.priority} onChange={(e) => set("priority", e.target.value)} className={inputCls} data-testid="pc-req-priority">
                    {["Low", "Normal", "High", "Urgent"].map((p) => <option key={p}>{p}</option>)}
                </select>
                <select value={form.project_id} onChange={(e) => set("project_id", e.target.value)} className={inputCls} data-testid="pc-req-project">
                    <option value="">-- Project (optional) --</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.code}</option>)}
                </select>
            </div>
            <div className="mt-3 text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Line Items</div>
            <table className="w-full font-mono-fin text-xs mt-1">
                <thead className="border-b border-panel">
                    <tr className="text-[9px] uppercase tracking-wider text-[var(--text-muted)]">
                        <th className="text-left py-1">Item</th><th>Unit</th><th className="text-right">Qty</th><th className="text-right">Est Rate</th><th></th>
                    </tr>
                </thead>
                <tbody>
                    {form.lines.map((l, i) => (
                        <tr key={i}>
                            <td className="py-1"><input value={l.item} onChange={(e) => setForm((f) => { const n = [...f.lines]; n[i] = { ...n[i], item: e.target.value }; return { ...f, lines: n }; })} className="w-full bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs" /></td>
                            <td><input value={l.unit} onChange={(e) => setForm((f) => { const n = [...f.lines]; n[i] = { ...n[i], unit: e.target.value }; return { ...f, lines: n }; })} className="w-20 bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs" /></td>
                            <td><input type="number" value={l.qty} onChange={(e) => setForm((f) => { const n = [...f.lines]; n[i] = { ...n[i], qty: e.target.value }; return { ...f, lines: n }; })} className="w-20 bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs text-right" /></td>
                            <td><input type="number" value={l.est_rate} onChange={(e) => setForm((f) => { const n = [...f.lines]; n[i] = { ...n[i], est_rate: e.target.value }; return { ...f, lines: n }; })} className="w-24 bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs text-right" /></td>
                            <td>{form.lines.length > 1 && <button onClick={() => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }))} className="text-neg"><Trash2 size={11} /></button>}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <button data-testid="pc-req-add-line" onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, { item: "", unit: "Bags", qty: 1, est_rate: 0 }] }))} className="mt-2 text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)]"><PlusCircle size={11} className="inline" /> Add Line</button>
            <textarea placeholder="Remarks" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} className={`${inputCls} mt-3`} rows={2} />
            <div className="mt-4 flex justify-end">
                <button data-testid="pc-req-save" onClick={submit} className="px-4 py-2 bg-[var(--brand-amber)] text-black font-mono-fin uppercase text-xs tracking-[0.15em] font-semibold">Save Requisition</button>
            </div>
        </Modal>
    );
}


function POForm({ vendors, projects, requisitions, onClose, onSaved }) {
    const [form, setForm] = useState({ date: today(), vendor_id: "", project_id: "", delivery_date: "", requisition_id: "", gst_rate: 18, remarks: "", lines: [{ item: "", unit: "Bags", qty: 1, rate: 0 }] });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // If requisition selected, prefill items
    useEffect(() => {
        if (form.requisition_id) {
            const r = requisitions.find((x) => x.id === form.requisition_id);
            if (r) setForm((f) => ({ ...f, lines: r.lines.map((l) => ({ item: l.item, unit: l.unit, qty: l.qty, rate: l.est_rate })), project_id: r.project_id || f.project_id }));
        }
    }, [form.requisition_id]);

    const subtotal = form.lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.rate || 0), 0);
    const gst = subtotal * (Number(form.gst_rate) / 100);
    const total = subtotal + gst;

    const submit = async () => {
        try {
            const vendor = vendors.find((v) => v.id === form.vendor_id);
            const project = projects.find((p) => p.id === form.project_id);
            const res = await api.post("/procurement/pos", {
                ...form,
                vendor_name: vendor?.name || "",
                project_code: project?.code || "",
                gst_rate: Number(form.gst_rate),
                lines: form.lines.map((l) => ({ ...l, qty: Number(l.qty), rate: Number(l.rate) })),
            });
            toast.success(`${res.data.po_no} raised`);
            onSaved();
        } catch (e) {
            toast.error("Failed: " + (e.response?.data?.detail || e.message));
        }
    };
    return (
        <Modal title="New Purchase Order" onClose={onClose}>
            <div className="grid grid-cols-2 gap-3">
                <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} data-testid="pc-po-date" />
                <input type="date" value={form.delivery_date} onChange={(e) => set("delivery_date", e.target.value)} placeholder="Delivery date" className={inputCls} data-testid="pc-po-delivery" />
                <select value={form.vendor_id} onChange={(e) => set("vendor_id", e.target.value)} className={inputCls} data-testid="pc-po-vendor">
                    <option value="">-- Select Vendor --</option>
                    {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
                <select value={form.project_id} onChange={(e) => set("project_id", e.target.value)} className={inputCls} data-testid="pc-po-project">
                    <option value="">-- Project (optional) --</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.code}</option>)}
                </select>
                <select value={form.requisition_id} onChange={(e) => set("requisition_id", e.target.value)} className={inputCls} data-testid="pc-po-req">
                    <option value="">-- Link Requisition (optional) --</option>
                    {requisitions.map((r) => <option key={r.id} value={r.id}>{r.req_no} · {r.department}</option>)}
                </select>
                <select value={form.gst_rate} onChange={(e) => set("gst_rate", e.target.value)} className={inputCls} data-testid="pc-po-gst">
                    {[0, 5, 12, 18, 28].map((r) => <option key={r} value={r}>GST @ {r}%</option>)}
                </select>
            </div>

            <div className="mt-3 text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Line Items</div>
            <table className="w-full font-mono-fin text-xs mt-1">
                <thead className="border-b border-panel">
                    <tr className="text-[9px] uppercase tracking-wider text-[var(--text-muted)]">
                        <th className="text-left py-1">Item</th><th>Unit</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">Amount</th><th></th>
                    </tr>
                </thead>
                <tbody>
                    {form.lines.map((l, i) => (
                        <tr key={i}>
                            <td className="py-1"><input value={l.item} onChange={(e) => setForm((f) => { const n = [...f.lines]; n[i] = { ...n[i], item: e.target.value }; return { ...f, lines: n }; })} className="w-full bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs" /></td>
                            <td><input value={l.unit} onChange={(e) => setForm((f) => { const n = [...f.lines]; n[i] = { ...n[i], unit: e.target.value }; return { ...f, lines: n }; })} className="w-20 bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs" /></td>
                            <td><input type="number" value={l.qty} onChange={(e) => setForm((f) => { const n = [...f.lines]; n[i] = { ...n[i], qty: e.target.value }; return { ...f, lines: n }; })} className="w-20 bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs text-right" /></td>
                            <td><input type="number" value={l.rate} onChange={(e) => setForm((f) => { const n = [...f.lines]; n[i] = { ...n[i], rate: e.target.value }; return { ...f, lines: n }; })} className="w-24 bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs text-right" /></td>
                            <td className="text-right text-amber">{fmtINR(Number(l.qty || 0) * Number(l.rate || 0))}</td>
                            <td>{form.lines.length > 1 && <button onClick={() => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }))} className="text-neg"><Trash2 size={11} /></button>}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <button data-testid="pc-po-add-line" onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, { item: "", unit: "Bags", qty: 1, rate: 0 }] }))} className="mt-2 text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)]"><PlusCircle size={11} className="inline" /> Add Line</button>

            <div className="mt-3 border border-panel p-3 bg-[var(--bg-panel-hover)] grid grid-cols-3 gap-3 font-mono-fin text-xs">
                <div><div className="text-[10px] text-[var(--text-muted)] uppercase">Subtotal</div><div className="text-[var(--text-primary)] mt-1">{fmtINR(subtotal)}</div></div>
                <div><div className="text-[10px] text-[var(--text-muted)] uppercase">GST @ {form.gst_rate}%</div><div className="text-amber mt-1">{fmtINR(gst)}</div></div>
                <div><div className="text-[10px] text-[var(--text-muted)] uppercase">Total</div><div className="text-pos mt-1 font-semibold">{fmtINR(total)}</div></div>
            </div>

            <textarea placeholder="Remarks / T&C" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} className={`${inputCls} mt-3`} rows={2} />
            <div className="mt-4 flex justify-end">
                <button data-testid="pc-po-save" onClick={submit} className="px-4 py-2 bg-[var(--brand-amber)] text-black font-mono-fin uppercase text-xs tracking-[0.15em] font-semibold">Save PO</button>
            </div>
        </Modal>
    );
}


function GRNForm({ pos, onClose, onSaved }) {
    const [form, setForm] = useState({ date: today(), po_id: "", lines: [], remarks: "" });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    useEffect(() => {
        if (form.po_id) {
            const p = pos.find((x) => x.id === form.po_id);
            if (p) setForm((f) => ({ ...f, lines: p.lines.map((l) => ({ item: l.item, unit: l.unit, qty_received: l.qty, rate: l.rate })) }));
        }
    }, [form.po_id]);

    const total = form.lines.reduce((s, l) => s + Number(l.qty_received || 0) * Number(l.rate || 0), 0);

    const submit = async () => {
        try {
            const po = pos.find((p) => p.id === form.po_id);
            if (!po) { toast.error("Select a PO first"); return; }
            const res = await api.post("/procurement/grns", {
                ...form,
                po_no: po.po_no,
                vendor_id: po.vendor_id,
                vendor_name: po.vendor_name,
                project_id: po.project_id,
                project_code: po.project_code,
                lines: form.lines.map((l) => ({ ...l, qty_received: Number(l.qty_received), rate: Number(l.rate) })),
            });
            toast.success(`${res.data.grn_no} received · Inventory auto-updated`);
            onSaved();
        } catch (e) {
            toast.error("Failed: " + (e.response?.data?.detail || e.message));
        }
    };

    return (
        <Modal title="New Goods Receipt Note (GRN)" onClose={onClose}>
            <div className="grid grid-cols-2 gap-3">
                <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} data-testid="pc-grn-date" />
                <select value={form.po_id} onChange={(e) => set("po_id", e.target.value)} className={inputCls} data-testid="pc-grn-po">
                    <option value="">-- Select Open PO --</option>
                    {pos.map((p) => <option key={p.id} value={p.id}>{p.po_no} · {p.vendor_name}</option>)}
                </select>
            </div>

            {form.lines.length > 0 && (
                <>
                    <div className="mt-3 text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Received Quantities (edit if partial)</div>
                    <table className="w-full font-mono-fin text-xs mt-1">
                        <thead className="border-b border-panel">
                            <tr className="text-[9px] uppercase tracking-wider text-[var(--text-muted)]">
                                <th className="text-left py-1">Item</th><th>Unit</th><th className="text-right">Qty Received</th><th className="text-right">Rate</th><th className="text-right">Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {form.lines.map((l, i) => (
                                <tr key={i}>
                                    <td className="py-1 text-[var(--text-primary)]">{l.item}</td>
                                    <td className="text-[var(--text-secondary)]">{l.unit}</td>
                                    <td><input type="number" value={l.qty_received} onChange={(e) => setForm((f) => { const n = [...f.lines]; n[i] = { ...n[i], qty_received: e.target.value }; return { ...f, lines: n }; })} className="w-24 bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs text-right" /></td>
                                    <td className="text-right text-[var(--text-secondary)]">{fmtINR(l.rate)}</td>
                                    <td className="text-right text-amber">{fmtINR(Number(l.qty_received || 0) * Number(l.rate || 0))}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="mt-3 border border-panel p-3 bg-[var(--bg-panel-hover)] font-mono-fin text-xs">
                        <div className="text-[10px] text-[var(--text-muted)] uppercase">Total GRN Value</div>
                        <div className="text-pos mt-1 font-semibold text-lg">{fmtINR(total)}</div>
                    </div>
                </>
            )}
            <textarea placeholder="Remarks" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} className={`${inputCls} mt-3`} rows={2} />
            <div className="mt-4 flex justify-end">
                <button data-testid="pc-grn-save" onClick={submit} disabled={!form.po_id} className="px-4 py-2 bg-[var(--brand-amber)] text-black font-mono-fin uppercase text-xs tracking-[0.15em] font-semibold disabled:opacity-50">Save GRN · Auto-post Inventory</button>
            </div>
        </Modal>
    );
}
